import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Account } from '@/types'

// Account listing: Express v1 (/api) + Laravel v2 (/api/v2) merged
// Flow/automation endpoints: Laravel v2 (/api/v2) — accounts live in MySQL
const V1 = '/api'
const V2 = '/api/v2'

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function normalizeTags(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((tag): tag is string => typeof tag === 'string') : undefined
}

async function v1get<T>(path: string): Promise<T> {
  const res = await fetch(`${V1}${path}`, { headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1post<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V1}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1put<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V1}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1del<T>(path: string): Promise<T> {
  const res = await fetch(`${V1}${path}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v2put<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V2}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v2post<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V2}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v2del<T>(path: string): Promise<T> {
  const res = await fetch(`${V2}${path}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export function useAccounts() {
  return useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: async () => {
      const [v1Accounts, v2Accounts] = await Promise.all([
        v1get<Account[]>('/accounts'),
        fetch('/api/v2/accounts', { headers: authHeaders() })
          .then(r => r.ok ? r.json() : [])
          .catch(() => []),
      ])
      // Build v2 lookup by igUserId and pageId for enrichment
      const v2ByIg = new Map<string, Record<string, unknown>>()
      const v2ByPage = new Map<string, Record<string, unknown>>()
      for (const acc of v2Accounts) {
        if (acc.igUserId) v2ByIg.set(String(acc.igUserId), acc)
        if (acc.pageId) v2ByPage.set(String(acc.pageId), acc)
      }

      // Enrich v1 accounts with v2 data (tags, etc.)
      const merged = v1Accounts.map(a => {
        const v2 = v2ByIg.get(String(a.igUserId)) || v2ByPage.get(String(a.pageId))
        if (v2) {
          return { ...a, tags: normalizeTags(v2.tags) ?? a.tags }
        }
        return a
      })

      // Add v2-only accounts (IG Login, OAuth v2) that don't exist in v1
      const v1Ids = new Set(v1Accounts.map(a => String(a.igUserId)))
      const v1PageIds = new Set(v1Accounts.map(a => String(a.pageId)))
      for (const acc of v2Accounts) {
        if (!v1Ids.has(String(acc.igUserId)) && !v1PageIds.has(String(acc.pageId))) {
          merged.push(acc)
        }
      }
      return merged
    },
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  })
}

export function useDisconnectAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (igUserId: string) => {
      // Delete from both v1 (JSON) and v2 (MySQL) — account may exist in either or both
      const [v1Result, v2Result] = await Promise.allSettled([
        v1del(`/accounts/${igUserId}`),
        v2del(`/accounts/${igUserId}`),
      ])
      // If both failed, throw
      if (v1Result.status === 'rejected' && v2Result.status === 'rejected') {
        throw new Error(v1Result.reason?.message || 'Failed to disconnect account')
      }
      return { ok: true }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] })
      qc.invalidateQueries({ queryKey: ['v1-accounts'] })
    },
  })
}

export function useToggleIG() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (igUserId: string) => v2post(`/accounts/${igUserId}/toggle-ig`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  })
}

export function useToggleFB() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (igUserId: string) => v2post(`/accounts/${igUserId}/toggle-fb`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  })
}

export function useApplyTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ igUserId, template }: { igUserId: string; template: unknown }) =>
      v2post(`/accounts/${igUserId}/apply-template`, { template }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  })
}

export function useSaveTags() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ igUserId, tags }: { igUserId: string; tags: string[]; igLogin?: boolean }) =>
      v2put(`/accounts/${igUserId}/tags`, { tags }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] })
      qc.invalidateQueries({ queryKey: ['v1-accounts'] })
    },
  })
}

export function useSaveAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ igUserId, ...data }: { igUserId: string; platform: string; autoCommentEnabled: boolean; autoCommentText: string; moderationEnabled: boolean; blockedWords: string[]; privateReplyEnabled?: boolean; privateReplyMessage?: string }) =>
      v2put(`/accounts/${igUserId}/automation`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  })
}

export function useSaveKeepAlive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ igUserId, ...data }: { igUserId: string; keepAliveEnabled: boolean; keepAliveMessage: string }) =>
      v2put(`/accounts/${igUserId}/keep-alive`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  })
}

export function useDeleteToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ igUserId, index }: { igUserId: string; index: number }) =>
      v2del(`/accounts/${igUserId}/token/${index}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  })
}

export function useDisconnectProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (profileName: string) => {
      // Delete from both v1 (JSON) and v2 (MySQL)
      const results = await Promise.allSettled([
        v1del<{ ok: boolean; tokensRemoved: number; accountsRemoved: number }>(`/accounts/profile/${encodeURIComponent(profileName)}`),
        v2del<{ ok: boolean; tokensRemoved: number; accountsRemoved: number }>(`/accounts/profile/${encodeURIComponent(profileName)}`),
      ])
      // Return the best result we got
      const v1 = results[0].status === 'fulfilled' ? results[0].value : null
      const v2 = results[1].status === 'fulfilled' ? results[1].value : null
      if (!v1 && !v2) throw new Error('Erro ao desconectar perfil')
      return {
        ok: true,
        tokensRemoved: (v1?.tokensRemoved ?? 0) + (v2?.tokensRemoved ?? 0),
        accountsRemoved: (v1?.accountsRemoved ?? 0) + (v2?.accountsRemoved ?? 0),
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] })
      qc.invalidateQueries({ queryKey: ['v1-accounts'] })
    },
  })
}
